import React, { useEffect, useState } from 'react';
import '../styles/overview.css';

import { useOutletContext } from 'react-router-dom';
import { ICONS } from '../../../assets/icons';

import OverviewChart from '../components/overviewChart/BroadcastPerformance';
import Performance from '../components/overviewChart/Performance';
import {
  overviewData,
  performanceData,
  performanceLeads,
  performanceSales
} from '../constants/overview';
import GaugeCharts from '../components/overviewChart/GaugeCharts';
import AreaChart from '../components/overviewChart/AreaChart';
import { Tooltip } from 'antd';

import { formatCount } from '../helpers';

const Overview = () => {
  const {
    performanceList,
    OverviewList,
    OverviewListTotalCount,
    overviewLoader,
    goalInfo,
    goalInfoDate,
    campaignOverviewData,
    handlePerformance,
    filter,
    captureRef,
    loader,
    startDate,
    endDate,
  } = useOutletContext();

  const performanceDataMapping =
    campaignOverviewData?.campaign_goal_type_id === 2
      ? performanceLeads 
      : campaignOverviewData?.campaign_goal_type_id === 4
      ? performanceSales
      : performanceData;

  const [updatedoverviewData, setUpdatedoverviewData] = useState(overviewData);
  const [updatedPerformanceData, setUpdatedPerformanceData] = useState(
    performanceDataMapping
  );

  useEffect(() => {
    if (OverviewList || OverviewListTotalCount) {
      setUpdatedoverviewData(
        overviewData?.map((item) => {
          // Handle OverviewList updates
          if (OverviewList) {
            switch (item.label) {
              case 'Delivered':
                return {
                  ...item,
                  count: OverviewList?.DELIVERED,
                  data: OverviewList?.delivered,
                };
              case 'Bounced':
                return {
                  ...item,
                  count: OverviewList?.FAILED,
                  data: OverviewList?.bounced,
                };
              case 'Read':
                return {
                  ...item,
                  count: OverviewList?.READ,
                  data: OverviewList?.read,
                };
              case 'Sent':
                return {
                  ...item,
                  count: OverviewList?.SENT,
                  data: OverviewList?.sent,
                };
              default:
                // Continue to check for OverviewListTotalCount
                break;
            }
          }

          // Handle OverviewListTotalCount updates
          if (OverviewListTotalCount && item.label === 'Audience') {
            return {
              ...item,
              count: OverviewListTotalCount?.TOTAL,
              data: OverviewListTotalCount?.total,
            };
          }

          return item;
        })
      );
    }

    if (performanceDataMapping) {
      setUpdatedPerformanceData(
        performanceDataMapping?.map((item) => {
          switch (item.label) {
            case 'Clicks':
              return { ...item, count: performanceList?.URL_CLICKED };
            case 'Reactions':
              return { ...item, count: performanceList?.REACTED };
            case 'Leads':
              return { ...item, count: performanceList?.LEAD };
            case 'Opt-out':
              return { ...item, count: performanceList?.OPTED_OUT };
            case 'Completion':
              return { ...item, count: performanceList?.COMPLETED };
            case 'Payment Pending':
              return { ...item, count: performanceList?.PENDING };
            case 'Payment Captured':
              return { ...item, count: performanceList?.COMPLETED };
            case 'Payment Failed':
              return { ...item, count: performanceList?.FAILED };
            default:
              return item;
          }
        })
      );
    }
  }, [OverviewList, performanceList, OverviewListTotalCount]);

  const goalTarget = goalInfo?.goal_target || 0;
  const goalAchieved = goalInfo?.goal_achieved || 0;

  const goalDifference = goalTarget - goalAchieved;
  const goalRound2 = goalDifference < 0 ? 0 : goalDifference;

  const goalRound = (() => {
    switch (goalInfo?.metric_type) {
      case 'PERCENTAGE':
        return `${Math.round(goalRound2)}%`;
      case 'CURRENCY':
        return `₹${Math.round(goalRound2).toLocaleString()}`;
      case 'NUMBER':
      default:
        return Math.round(goalRound2);
    }
  })();

      return (
        <div className="h-full max-h-[72vh] overflow-y-auto">
          <div className="flex flex-col gap-5 overflow-hidden" ref={captureRef}>
            <div className="flex flex-col gap-4">
              {/* Top row: Overview grid + Goal */}
              <div className="flex w-full justify-between gap-2">
                {/* Overview grid */}
                <div className="flex-1">
                  <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
                    {updatedoverviewData?.map((item) => (
                      <div
                        key={item?.id}
                        className="bg-white rounded-2xl flex flex-col pt-4 justify-between"
                      >
                        <div>
                          <Tooltip title={`${item?.toolTip}: ${item?.count ?? 0}`}>
                            <div className="flex items-center gap-2 px-4">
                              {item?.icon && (
                                typeof item.icon === 'string' ? (
                                  <img src={item.icon} alt={item.label} className="w-[28px] h-[28px]" /> 
                                ) : (
                                  <item.icon className="w-10 h-10" color={item?.color} />
                                )
                              )}
                              <span className="no-search-body">{item?.label}</span>
                              <img
                                src={ICONS?.conversationAction}
                                alt="info"
                                className="cursor-pointer w-[18px] ml-auto"
                              />
                            </div>
                          </Tooltip>

                          {loader ? (
                            <div className="flex justify-center items-center py-8">
                              <div className="loader" />
                            </div>
                          ) : (
                            <div className="pt-4">
                              <div
                                className={`px-4 min-h-[40px] flex items-center ${
                                  item?.count === undefined ? 'main-header-light' : 'main-header'
                                }`}
                                >
                                {item?.label === 'Audience' && overviewLoader ? (
                                  <div className="flex justify-center items-center">
                                    <div className="loader" />
                                  </div>
                                ) : (
                                  item?.count || 0
                                )}
                              </div>

                              <div className="px-1">
                                <AreaChart
                                  data={item?.data}
                                  color={item?.color}
                                  name={item?.label}
                                  startDate={startDate}
                                  endDate={endDate}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Goal info */}
                <div className="w-[28%] min-h-[44vh] bg-white rounded-2xl flex flex-col px-6 pt-8 overflow-hidden">
                  <div className="flex items-center gap-2 border-b pb-2">
                    <span className="chart-headers">Goal Info</span>
                    <Tooltip
                      title={`${
                        goalInfo?.goal_evaluation_type
                          ? goalInfo?.goal_evaluation_type?.charAt(0).toUpperCase() +
                            goalInfo?.goal_evaluation_type?.slice(1).toLowerCase()
                          : '—'
                      } Counts`}
                    >
                      <img
                        src={ICONS?.conversationAction}
                        alt="info"
                        className="cursor-pointer pl-2"
                      />
                    </Tooltip>
                  </div>
      
                  {loader ? (
                    <div className="flex flex-1 items-center justify-center">
                      <div className="loader" />
                    </div>
                  ) : (
                    <div className="flex flex-col justify-between min-h-[36vh] pt-6">
                      <GaugeCharts
                        id="gauge-chart1"
                        goalInfo={goalInfo}
                        campaignOverviewData={campaignOverviewData}
                      />
      
                      <div className="mt-6 grid grid-cols-2 gap-4">
                        <div className="flex flex-col items-center">
                          <span
                            className={`pt-2 flex items-center gap-2 ${
                              goalRound === 0 ? 'main-header-light' : 'main-header'
                            }`}
                          >
                            <img
                              src={ICONS?.goalTargetInfo}
                              alt="Targets"
                              className="w-6 h-6"
                            />
                            {goalRound}
                          </span>
                          <span className="new-sidebar mt-1">Targets remaining</span>
                        </div>
      
                        <div className="flex flex-col items-center">
                          <span
                            className={`pt-2 flex items-center gap-2 ${
                              goalInfo?.progress_status === 'No data found'
                                ? 'main-header-light'
                                : 'main-header'
                            }`}
                          >
                            <img
                              src={ICONS?.goalRemainingDays}
                              alt="Days remaining"
                              className="w-6 h-6"
                            />
                            {goalInfo?.progress_status === 'No data found' ? 0 : goalInfoDate}
                          </span>
                          <span className="new-sidebar mt-1">Days remaining</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Performance */}
              <div className="w-full pt-4 bg-white rounded-2xl flex flex-col">
                <div className="w-full min-h-[50vh]">
                  <div className="flex items-center justify-between px-4 pt-5 pb-2 border-b">
                    <p className="chart-headers">Performance</p>
                  </div>
      
                  {loader ? (
                    <div className="flex items-center justify-center" style={{ height: '34vh' }}>
                      <div className="loader" />
                    </div>
                  ) : (
                    <>
                      <Performance
                        performanceList={performanceList}
                        startDate={startDate}
                        endDate={endDate}
                        updatedPerformanceData={updatedPerformanceData}
                      />
      
                      <div className="flex flex-row items-stretch text-[var(--textBlack)] mt-2 pt-1">
                        {updatedPerformanceData?.map((item) => {
                          const isActive = filter?.includes(item?.value);
                          return (
                            <button
                              key={item?.id}
                              type="button"
                              onClick={() => handlePerformance?.(item?.value)}
                              className="flex-1 border-r last:border-r-0 px-2 py-3"
                            >
                              <div className={`rounded-lg hover:bg-[#F4F4F5] transition-colors py-2 ${isActive ? 'bg-[#E8E8EA]' : ''}`}>
                                <div className="flex items-center justify-center gap-2 px-3 pt-3">
                                {item?.icon && (
                                  typeof item.icon === 'string' ? (
                                    <img src={item.icon} alt={item.label} className="w-5 h-5" /> 
                                  ) : (
                                    <item.icon className="w-5 h-5" />
                                  )
                                )}
                                  <span className="no-search-body">{item?.label}</span>
                                  <Tooltip
                                    title={`Overall ${
                                      item?.label === 'Clicks' ? 'Website Clicks' : item?.label
                                    } Count`}
                                  >
                                    <img
                                      src={ICONS?.conversationAction}
                                      alt="info"
                                      className="w-4 h-4"
                                    />
                                  </Tooltip>
                                </div>
                                <div className="flex justify-center pt-3">
                                  <span
                                    className={`${
                                      item?.count === undefined ? 'count-style-light' : 'count-style'
                                    }`}
                                  >
                                    {item?.count ?? 0}
                                  </span>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Overview chart */}
              <div className="w-full bg-white rounded-2xl flex flex-col py-4">
                <OverviewChart />
              </div>
            </div>
          </div>
        </div>
      );
};

export default Overview;
